import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "./ui/sheet";

export const CartDrawer = () => {
  const {
    items,
    isOpen,
    setOpen,
    subtotal,
    shipping,
    total,
    removeItem,
    updateQty,
  } = useCart();
  const navigate = useNavigate();

  const goToCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-white p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-5 border-b border-[color:var(--ssm-border)] flex flex-row items-center justify-between space-y-0">
          <SheetTitle className="font-display text-2xl font-medium">Your Cart</SheetTitle>
          <SheetDescription className="sr-only">
            Your shopping cart with items you've added. Review, update quantities, or proceed to checkout.
          </SheetDescription>
          <button
            data-testid="cart-close-btn"
            onClick={() => setOpen(false)}
            aria-label="Close cart"
            className="p-1"
          >
            <X size={18} />
          </button>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <p className="font-display text-2xl mb-2">Your cart is empty</p>
            <p className="text-sm text-neutral-500 mb-6">
              Timeless essentials, ready when you are.
            </p>
            <button
              data-testid="cart-empty-shop-btn"
              className="inline-flex items-center justify-center px-8 py-3 bg-black text-white text-xs uppercase tracking-[0.2em]"
              onClick={() => {
                setOpen(false);
                navigate("/shop");
              }}
            >
              Shop now
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 divide-y divide-[color:var(--ssm-border)]">
              {items.map((item) => (
                <div
                  key={`${item.product_id}-${item.size}-${item.color || "default"}`}
                  data-testid={`cart-item-${item.slug || item.product_id}`}
                  className="flex gap-4 py-4"
                >
                  <Link
                    to={`/product/${item.slug}`}
                    onClick={() => setOpen(false)}
                    className="shrink-0"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-20 h-28 object-cover bg-neutral-100"
                    />
                  </Link>
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between gap-3">
                      <Link
                        to={`/product/${item.slug}`}
                        onClick={() => setOpen(false)}
                        className="text-sm font-medium leading-tight hover:underline"
                      >
                        {item.name}
                      </Link>
                      <span className="text-sm">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                    <div className="text-xs text-neutral-500 mt-1">
                      {item.color ? `${item.color} · ` : ""}Size {item.size}
                    </div>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="inline-flex items-center border border-[color:var(--ssm-border)]">
                        <button
                          data-testid={`cart-qty-dec-${item.slug}`}
                          onClick={() =>
                            updateQty(item.product_id, item.size, item.quantity - 1, item.color)
                          }
                          className="p-2"
                          aria-label="Decrease quantity"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="px-3 text-sm">{item.quantity}</span>
                        <button
                          data-testid={`cart-qty-inc-${item.slug}`}
                          onClick={() =>
                            updateQty(item.product_id, item.size, item.quantity + 1, item.color)
                          }
                          className="p-2"
                          aria-label="Increase quantity"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <button
                        data-testid={`cart-remove-${item.slug}`}
                        onClick={() => removeItem(item.product_id, item.size, item.color)}
                        className="text-xs text-neutral-500 hover:text-black inline-flex items-center gap-1"
                      >
                        <Trash2 size={12} /> Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[color:var(--ssm-border)] px-6 py-5 space-y-3 bg-[color:var(--ssm-surface)]">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600">Subtotal</span>
                <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600">Shipping</span>
                <span>{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span>
              </div>
              <div className="flex justify-between text-base font-medium pt-2 border-t border-[color:var(--ssm-border)]">
                <span>Total</span>
                <span data-testid="cart-total">${total.toFixed(2)}</span>
              </div>
              <button
                data-testid="cart-checkout-btn"
                onClick={goToCheckout}
                className="w-full py-4 bg-black text-white text-xs uppercase tracking-[0.25em] hover:bg-neutral-800 transition-colors"
              >
                Checkout
              </button>
              <p className="text-[11px] text-neutral-500 text-center">
                Secure checkout · Encrypted payments · Powered by Stripe
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
